import { useState, useCallback, useEffect } from 'react';
import { TransformType, TransformConfig, ValueMapping } from '../models';

interface FieldConfigSelector {
  selector: string;
  attribute?: string;
  transform?: TransformConfig;
  defaultValue?: any;
  valueMappings?: ValueMapping[];
}

interface UseFieldFormStateProps {
  selector: FieldConfigSelector;
  onUpdate: (selector: FieldConfigSelector) => void;
  isMultiple: boolean;
  isActive?: boolean;
}

export const useFieldFormState = ({
  selector,
  onUpdate,
  isMultiple,
  isActive = true,
}: UseFieldFormStateProps) => {
  const [localValues, setLocalValues] = useState({
    selector:
      selector.selector === '__PLACEHOLDER__' ? '' : selector.selector || '',
    attribute: selector.attribute || '',
    defaultValue: selector.defaultValue || '',
    transformPattern: selector.transform?.pattern || '',
    transformDelimiter: selector.transform?.delimiter || '',
    transformFunction: selector.transform?.function || '',
    subSelector: selector.transform?.subSelector || '',
    subAttribute: selector.transform?.subAttribute || '',
    subDefaultValue: selector.transform?.subDefaultValue || '',
    subTransformDelimiter: selector.transform?.subTransform?.delimiter || '',
    subTransformPattern: selector.transform?.subTransform?.pattern || '',
    subTransformFunction: selector.transform?.subTransform?.function || '',
  });

  // Update local values when selector changes
  useEffect(() => {
    setLocalValues({
      selector:
        selector.selector === '__PLACEHOLDER__'
          ? ''
          : selector.selector || '',
      attribute: selector.attribute || '',
      defaultValue: selector.defaultValue || '',
      transformPattern: selector.transform?.pattern || '',
      transformDelimiter: selector.transform?.delimiter || '',
      transformFunction: selector.transform?.function || '',
      subSelector: selector.transform?.subSelector || '',
      subAttribute: selector.transform?.subAttribute || '',
      subDefaultValue: selector.transform?.subDefaultValue || '',
      subTransformDelimiter:
        selector.transform?.subTransform?.delimiter || '',
      subTransformPattern: selector.transform?.subTransform?.pattern || '',
      subTransformFunction: selector.transform?.subTransform?.function || '',
    });
  }, [selector]);

  // Sync selector when becoming active
  useEffect(() => {
    if (!isActive) {
      return;
    }

    setLocalValues((prev) => ({
      ...prev,
      selector:
        selector.selector === '__PLACEHOLDER__'
          ? ''
          : selector.selector || '',
    }));
  }, [isActive, selector.selector]);

  const updateLocalValue = useCallback((key: string, value: string) => {
    setLocalValues((prev) => {
      if (prev[key as keyof typeof prev] === value) return prev;
      return { ...prev, [key]: value };
    });
  }, []);

  const updateSelector = useCallback(
    (updates: Partial<FieldConfigSelector>) => {
      onUpdate({
        ...selector,
        ...updates,
      });
    },
    [selector, onUpdate]
  );

  const handleSelectorBlur = useCallback(() => {
    updateSelector({ selector: localValues.selector });
  }, [localValues.selector, updateSelector]);

  const handleAttributeBlur = useCallback(() => {
    updateSelector({ attribute: localValues.attribute || undefined });
  }, [localValues.attribute, updateSelector]);

  const handleDefaultValueBlur = useCallback(() => {
    const defaultValue =
      localValues.defaultValue === ''
        ? undefined
        : isMultiple
        ? localValues.defaultValue.split(',')
        : localValues.defaultValue;
    updateSelector({ defaultValue });
  }, [localValues.defaultValue, isMultiple, updateSelector]);

  const handleValueMappingsChange = useCallback(
    (valueMappings: ValueMapping[]) => {
      updateSelector({ valueMappings });
    },
    [updateSelector]
  );

  const handleTransformTypeChange = useCallback(
    (type: TransformType) => {
      const currentTransform = selector.transform || { type: 'none' };
      const updatedTransform = {
        ...currentTransform,
        type,
      };

      if (type === 'custom' && !currentTransform.function) {
        updatedTransform.function = 'return value;';
      }

      updateSelector({ transform: updatedTransform });
    },
    [selector.transform, updateSelector]
  );

  const handleTransformParamBlur = useCallback(
    (param: string) => {
      const value = localValues[
        `transform${
          param.charAt(0).toUpperCase() + param.slice(1)
        }` as keyof typeof localValues
      ] as string;
      const currentTransform = selector.transform || { type: 'none' };

      const updatedTransform = {
        ...currentTransform,
        [param]: value,
      };

      updateSelector({ transform: updatedTransform });
    },
    [localValues, selector.transform, updateSelector]
  );

  const handleSubTransformTypeChange = useCallback(
    (type: TransformType) => {
      const currentTransform = selector.transform || { type: 'load' };
      const currentSubTransform = currentTransform.subTransform || {
        type: 'none',
      };

      const updatedTransform = {
        ...currentTransform,
        subTransform: {
          ...currentSubTransform,
          type,
        },
      };

      updateSelector({ transform: updatedTransform });
    },
    [selector.transform, updateSelector]
  );

  const handleSubTransformParamBlur = useCallback(
    (param: string) => {
      const value = localValues[
        `subTransform${
          param.charAt(0).toUpperCase() + param.slice(1)
        }` as keyof typeof localValues
      ] as string;
      const currentTransform = selector.transform || { type: 'load' };
      const currentSubTransform = currentTransform.subTransform || {
        type: 'none',
      };

      const updatedTransform = {
        ...currentTransform,
        subTransform: {
          ...currentSubTransform,
          [param]: value,
        },
      };

      updateSelector({ transform: updatedTransform });
    },
    [localValues, selector.transform, updateSelector]
  );

  const handleSubParamBlur = useCallback(
    (param: 'subSelector' | 'subAttribute' | 'subDefaultValue') => {
      const value = localValues[param];
      const currentTransform = selector.transform || { type: 'load' };

      const updatedTransform = {
        ...currentTransform,
        [param]: value,
      };

      updateSelector({ transform: updatedTransform });
    },
    [localValues, selector.transform, updateSelector]
  );

  return {
    localValues,
    updateLocalValue,
    handleSelectorBlur,
    handleAttributeBlur,
    handleDefaultValueBlur,
    handleValueMappingsChange,
    handleTransformTypeChange,
    handleTransformParamBlur,
    handleSubTransformTypeChange,
    handleSubTransformParamBlur,
    handleSubParamBlur,
  };
};
